import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, Eye, EyeOff, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useApiWakeUp } from '../hooks/useApiWakeUp';
import { WakingOverlay } from '../components/WakingOverlay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const apiStatus = useApiWakeUp();

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('Missing reset token. Please use the link from your email.');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirm) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setDone(true);
      toast.success('Password reset successfully');
      setTimeout(() => navigate('/admin/login'), 2500);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Could not reset password. The link may be invalid or expired.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center p-6" data-testid="reset-password-page">
      {apiStatus === 'waking' && <WakingOverlay />}
      <Card className="w-full max-w-md border-2 border-[#8B7355]/20">
        <CardHeader>
          <CardTitle className="font-serif text-3xl text-[#8B7355] italic text-center">
            Set a New Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center space-y-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-[#8B7355] text-lg">Password updated</p>
              <p className="text-[#8B7355]/70 text-sm">Redirecting you to login...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              <p className="text-sm text-[#8B7355]/80 mb-4">
                Choose a strong new password (at least 8 characters).
              </p>

              <div>
                <Label htmlFor="newPassword" className="flex items-center gap-2 text-[#8B7355] mb-2">
                  <Lock className="w-4 h-4" /> New Password
                </Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    name="reset-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="border-[#8B7355]/30 pr-12"
                    placeholder="At least 8 characters"
                    data-testid="reset-new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-[#8B7355]/70 hover:text-[#8B7355]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="reset-toggle-pw"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm" className="flex items-center gap-2 text-[#8B7355] mb-2">
                  <Lock className="w-4 h-4" /> Confirm New Password
                </Label>
                <Input
                  id="confirm"
                  name="reset-password-confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="border-[#8B7355]/30"
                  placeholder="Re-enter password"
                  data-testid="reset-confirm-password"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8B7355] hover:bg-[#6B5845] text-white py-6 rounded-none font-serif text-lg"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/admin/login"
                  className="text-sm text-[#8B7355]/70 hover:text-[#8B7355] inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
